import { useApolloClient } from '@apollo/client/react';
import { useEffect } from 'react';
import { NOTIFICATIONS } from '../../../graphql/notification';
import { GeneralStatus, useCurrentUserQuery, useProjectExportsLazyQuery } from '../../../types/graphqlTypes';
import { pusher } from '../../pusher';
import { useDownloadExport } from '../../useDownloadExport';
import { useToastNotifications, ToastType } from '../../useToastNotifications';
import { useSeenExports } from '../../useSeenExports';
import { useT } from '../../../translation/src';
import type { ApolloClient } from '@apollo/client';

interface ExportDoneEvent {
  projectId: string;
  exportId: string;
  exportType: 'exportPointcloud';
  status: GeneralStatus;
}

function bindNotifications(channel: ReturnType<typeof pusher.subscribe>, apolloClient: ApolloClient) {
  channel.bind('notification', () => {
    // Use refetchQueries so every component observing the NOTIFICATIONS query
    // (header bell, modal, account select) receives fresh data immediately.
    apolloClient.refetchQueries({ include: [NOTIFICATIONS] }).catch((e) => console.error('Error refetching notifications:', e));
  });
}

interface BindExportsArgs {
  channel: ReturnType<typeof pusher.subscribe>;
  fetchExports: ReturnType<typeof useProjectExportsLazyQuery>[0];
  downloadExport: ReturnType<typeof useDownloadExport>['downloadExport'];
  showToast: ReturnType<typeof useToastNotifications>['showToast'];
  markSeen: (projectId: string, exportId: string) => void;
  readyTitle: string;
  readyBody: string;
  failedTitle: string;
  failedBody: string;
  downloadButtonText: string;
}

function bindExports({ channel, fetchExports, downloadExport, showToast, markSeen, readyTitle, readyBody, failedTitle, failedBody, downloadButtonText }: BindExportsArgs) {
  channel.bind('export-done', async (event: ExportDoneEvent) => {
    const { projectId: exportProjectId, exportId, status } = event;

    // Refresh cache so the dropdown reflects the new status
    await fetchExports({ variables: { projectId: exportProjectId } });

    if (status === GeneralStatus.Error) {
      showToast({ type: ToastType.ERROR, title: failedTitle, text: failedBody });
      return;
    }

    if (status !== GeneralStatus.Success) return;

    // Filename is resolved by the server's Content-Disposition header on the download route.
    showToast({
      type: ToastType.SUCCESS,
      title: readyTitle,
      text: readyBody,
      buttonText: downloadButtonText,
      actionBinding: () => {
        markSeen(exportProjectId, exportId);
        downloadExport({ projectId: exportProjectId, exportId }).catch((e) => console.error('Error downloading export:', e));
      },
    });
  });
}

/**
 * Global hook for user-channel events (notifications, export completion toasts).
 */
export const useUserEvents = () => {
  const apolloClient = useApolloClient();
  const { data: userData } = useCurrentUserQuery();
  const userId = userData?.currentUser?.id;

  const [fetchExports] = useProjectExportsLazyQuery({ fetchPolicy: 'network-only' });
  const { downloadExport } = useDownloadExport();
  const { showToast } = useToastNotifications();
  const { markSeen } = useSeenExports();

  const readyTitle = useT('Export ready', { swc: true });
  const readyBody = useT('Your export is ready to download.', { swc: true });
  const failedTitle = useT('Export failed', { swc: true });
  const failedBody = useT('Something went wrong while preparing your export.', { swc: true });
  const downloadButtonText = useT('download', { swc: true });

  useEffect(() => {
    if (!userId) return;

    const channel = pusher.subscribe(`user-${userId}`);

    bindNotifications(channel, apolloClient);
    bindExports({ channel, fetchExports, downloadExport, showToast, markSeen, readyTitle, readyBody, failedTitle, failedBody, downloadButtonText });

    return () => {
      channel.unbind_all();
      channel.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
};
